import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseDataProps } from './types';
import { AllFieldValueTypes, HierarchicalChoice, HierarchicalDesc } from '90.quickConnect.Models/models';
import { useStore } from '30.quickConnect.Stores';
import useEffectOnce from '80.quickConnect.Core/hooks/useEffectOnce';
import { useQCUpdateFunctionsContext } from '20.formLib/DeclarationContainer/contexts';
import { flatten } from '80.quickConnect.Core/helpers';
import { MAX_HIERARCHICAL_PREFETCH } from '80.quickConnect.Core/const';

const useData = (
  hierarchicalDesc: HierarchicalDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  setSelectedIndex: Dispatch<SetStateAction<string | undefined>>,
): UseDataProps => {
  // On récupère le store
  const {
    SharedListStore: { getSharedList },
  } = useStore();

  const { updateProperty } = useQCUpdateFunctionsContext();

  // On récupère la translation
  const { t } = useTranslation('declaration');
  const { listId, listChoice, fullPathId, value, filterDef } = hierarchicalDesc;
  const [hierarchicalChoices, setHierarchicalChoices] = useState<HierarchicalChoice[] | undefined>(() => {
    if (listChoice) {
      return listChoice;
    }
    return undefined;
  });
  const [deletedItem, setDeleteItem] = useState<string>();
  const [isDeleteAll, setIsDeleteAll] = useState<boolean>(false);
  // Récupération des hierarchicalChoices
  const lookedForList = useRef(false);

  useEffect(() => {
    if (deletedItem) {
      setSelectedIndex(undefined);
    }
  }, [deletedItem, setSelectedIndex]);

  useEffectOnce(() => {
    if (listChoice && listChoice.length > 0) {
      setHierarchicalChoices(listChoice);
      lookedForList.current = true;
      return;
    }

    if (listId && lookedForList.current === false) {
      getSharedList(listId).then((sharedList) => {
        if (sharedList) {
          const choices = listChoice ?? sharedList.hierarchicalChoices;

          const choicesLength = flatten(sharedList.hierarchicalChoices, (n) => n?.children ?? []).filter(
            (n) => !n?.children || n.children.length === 0,
          ).length;

          setHierarchicalChoices(choices);

          // On ne pousse dans l'arbre que si la hiérarchie n'est pas “géante”
          if (choicesLength <= MAX_HIERARCHICAL_PREFETCH) {
            updateProperty(fullPathId, 'listChoice', choices);
          }
        } else {
          // Liste introuvable : aucun choix
          setHierarchicalChoices([]);
        }
      });
      lookedForList.current = true;
    }
  });

  const handleDeleteAll = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      updateDeclaration(fullPathId, []);
      setIsDeleteAll(true);
      setSelectedIndex(undefined);
    },
    [fullPathId, setSelectedIndex, updateDeclaration],
  );

  useEffect(() => {
    if (listChoice && filterDef) {
      setHierarchicalChoices(listChoice);
    }
  }, [filterDef, listChoice]);
  // Construction de filteredValues avec label traduit depuis hierarchicalChoices
  function extractAllChoices(choices?: HierarchicalChoice[]): HierarchicalChoice[] {
    if (!choices) return [];
    let result: HierarchicalChoice[] = [];
    for (const node of choices) {
      if (node.value) result.push(node);
      if (Array.isArray(node.children) && node.children.length > 0) {
        result = result.concat(extractAllChoices(node.children));
      }
    }
    return result;
  }
  let filteredValues: HierarchicalChoice[] = [];
  if (Array.isArray(hierarchicalDesc.value) && Array.isArray(hierarchicalChoices)) {
    const allChoices = extractAllChoices(hierarchicalChoices);
    filteredValues = (hierarchicalDesc.value as HierarchicalChoice[])
      .map((v) => {
        const found = allChoices.find((choice) => choice.value === v.value);
        if (found) {
          return { value: found.value, label: found.label };
        }
        return undefined;
      })
      .filter((v): v is HierarchicalChoice => !!v);
  }

  return {
    t,
    hierarchicalChoices,
    deletedItem,
    setDeleteItem,
    handleDeleteAll,
    setIsDeleteAll,
    isDeleteAll,
    filteredValues,
  };
};

export default useData;
